import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Button from '@mui/material/Button';
import WarningIcon from '@mui/icons-material/Warning';
import clsx from 'clsx';
import Link from '@mui/material/Link';
import { FormattedMessage, useIntl } from 'react-intl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import filter from 'lodash/filter';
import { fromOrganizations } from '../../../store';
import { AvailableSubscription, SubscriptionInvitation, unlimitedAssignableSubs } from '../utils';
import { OrganizationFeature, isVisualTestingFeature, isHybridFeature } from '../../../models/organizationFeature';
import { ReactComponent as KREIcon } from './logo-license/kre.svg';
import { ReactComponent as KSEIcon } from './logo-license/kse.svg';
import { ReactComponent as TestOpsIcon } from './logo-license/testops.svg';
import CustomSwitch from '../../../components/switch/CustomSwitch';
import { TestOpsProject } from '../../../models';
import { resolvePath } from '../../../routes';
import { projectManagement as projectManagementPath } from '../../../layout/routes';

const useStyle = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
  },
  title: {
    fontWeight: 'bold',
    textAlign: 'center',
    marginTop: theme.spacing(3),
  },
  sectionProjectWrapper: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  labelSection: {
    color: '#233145',
    fontWeight: '500',
    '& > .required-asterisk': {
      color: '#db3131',
    },
  },
  projectContainer: {
    maxWidth: theme.spacing(54),
    flexDirection: 'column',
  },
  project: {
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(52),
  },
  projectWrapper: {
    padding: theme.spacing(1, 0),
    maxWidth: theme.spacing(54),
  },
  selectionProjects: {
    flex: 1,
  },
  selectionAction: {
    backgroundColor: '#FFFFFF',
    height: theme.spacing(4),
    fontWeight: 600,
    color: '#1847ca',
    '& > span': {
      fontWeight: 'normal !important',
      fontSize: '0.925rem !important',
      paddingLeft: theme.spacing(1.5),
    },
  },
  projectsRoot: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.5,
    overflow: 'auto',
    maxHeight: theme.spacing(8),
  },
  projectChip: {
    float: 'left',
    margin: theme.spacing(0.25),
    maxWidth: 200,
  },
  projectLabel: {
    fontSize: '0.925rem',
  },
  projectSelectedItem: {
    backgroundColor: '#FFFFFF !important',
    color: '#284169 !important',
    '& > div > span': {
      fontWeight: 'normal !important',
      fontSize: '0.925rem !important',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  projectItemText: {
    '& > span': {
      fontWeight: 'normal !important',
      fontSize: '0.925rem !important',
      color: '#284169 !important',
    },
  },
  menuPaper: {
    maxHeight: theme.spacing(24),
    maxWidth: theme.spacing(45),
  },
  buttonRefresh: {
    marginLeft: theme.spacing(0.5),
  },
  iconRefresh: {
    fontSize: theme.spacing(2.5),
    color: theme.typography.subtitle2.color,
  },
  sectionLicenseWrapper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2.5),
  },
  licenseContainer: {
    maxWidth: theme.spacing(54),
    flexDirection: 'column',
  },
  license: {
    display: 'flex',
    alignItems: 'center',
  },
  licenseWrapper: {
    padding: theme.spacing(1, 0),
    maxWidth: theme.spacing(60),
  },
  licenseName: {
    marginLeft: theme.spacing(1),
    color: '#1D3066',
  },
  message: {
    marginLeft: theme.spacing(4.5),
    marginTop: theme.spacing(1),
    paddingTop: 0,
    fontSize: theme.spacing(1.5),
    color: '#516393',
    maxHeight: theme.spacing(7.5),
    minHeight: theme.spacing(3.75),
    maxWidth: theme.spacing(43.75),
    lineHeight: '1.41',
  },
  infoIcon: {
    marginLeft: theme.spacing(1),
    fontSize: theme.spacing(2.5),
    color: theme.typography.subtitle2.color,
  },
  disabledSwitch: {
    opacity: '0.5',
  },
  licenseInfo: {
    display: 'flex',
    flex: 1,
    marginLeft: theme.spacing(1),
    maxWidth: theme.spacing(43.75),
  },
  button: {
    margin: theme.spacing(1, 0),
    width: theme.spacing(40),
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
    '& > span': {
      justifyContent: 'center',
    },
  },
  warning: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
  },
  warningMessage: {
    color: theme.palette.warning.main,
    fontSize: theme.spacing(1.25),
    paddingTop: 0,
    height: theme.spacing(3.75),
    maxHeight: theme.spacing(3.75),
  },
  warningIcon: {
    color: theme.palette.warning.main,
    fontSize: theme.spacing(2),
    marginRight: theme.spacing(0.5),
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0 auto',
  },
  buttonConfirm: {
    backgroundColor: '#1847ca',
    marginTop: 0,
  },
  buttonBack: {
    color: '#276ef1',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  occupiedSpace: {
    marginTop: theme.spacing(1),
    height: theme.spacing(3.75),
  },
}));

const kSEproducts = [
  OrganizationFeature.KSE,
  OrganizationFeature.UNLIMITED_KSE,
  OrganizationFeature.PER_USER_KSE,
];
const kREProducts = [
  OrganizationFeature.ENGINE,
  OrganizationFeature.UNLIMITED_ENGINE,
  OrganizationFeature.FLOATING_ENGINE,
];
const PATH_KSE_INFO = 'https://docs.katalon.com/docs/legacy/products-and-licenses/katalon-studio-enterprise-and-runtime-engine-licenses/sunsetting-plan-for-node-locked-and-floating-licenses';
const PATH_KSE_FLOATING = 'https://docs.katalon.com/docs/legacy/products-and-licenses/katalon-studio-enterprise-and-runtime-engine-licenses/sunsetting-plan-for-node-locked-and-floating-licenses';
const PATH_KRE_INFO = 'https://docs.katalon.com/docs/legacy/products-and-licenses/katalon-studio-enterprise-and-runtime-engine-licenses/sunsetting-plan-for-node-locked-and-floating-licenses';
const PATH_UNLIMITED_ENGINE = 'https://docs.katalon.com/docs/administer/katalon-studio-enterprise-and-katalon-runtime-engine-license/katalon-runtime-engine-floating-license';
const PATH_KSE_PER_USER = 'https://docs.katalon.com/docs/administer/katalon-studio-enterprise-and-katalon-runtime-engine-license/katalon-studio-enterprise-per-user-license#ariaid-title1';
const PATH_LICENSE_OVERVIEW = 'https://docs.katalon.com/docs/administer/katalon-studio-enterprise-and-katalon-runtime-engine-license/license-overview';
const PATH_TESTOPS_PLATFORM_PLAN = 'https://docs.katalon.com/docs/administer/katalon-platform-packages/katalon-platform-plans';

interface SelectProjectsAndLicensesProps {
  orgId: number;
  availableSubs: AvailableSubscription[];
  countUsers: number;
  onBack: () => void;
  onSubmit: () => void;
  handleSelectedSub: (subs: SubscriptionInvitation[]) => void;
  selectedSubs: SubscriptionInvitation[];
  projects: TestOpsProject[];
  selectedProjects: number[];
  handleSelectedProjects: (projectIds: number[]) => void;
  handleRefreshProjects: () => Promise<void>;
}

const SelectProjectsAndLicenses = (props: SelectProjectsAndLicensesProps) => {
  const classes = useStyle();
  const intl = useIntl();
  const {
    orgId,
    availableSubs,
    countUsers,
    onBack,
    onSubmit,
    handleSelectedSub,
    selectedSubs,
    selectedProjects,
    handleSelectedProjects,
    handleRefreshProjects,
    projects,
  } = props;
  const organization = useSelector(fromOrganizations.selectSelectedOrganization);
  const [allowedSubmit, setAllowedSubmit] = useState(true);
  // check if both KRE NL and F are in available:
  const isContainedNLandF = () => availableSubs.filter(
    it => (
      it.feature === OrganizationFeature.ENGINE
        || it.feature === OrganizationFeature.UNLIMITED_ENGINE
    ),
  ).length > 1;
  const isEmptyProjects = isEmpty(projects);
  const isSelectAllProjects = selectedProjects.length === projects.length;
  const isSelectNoProject = isEmpty(selectedProjects);

  useEffect(() => {
    // check if there's any default enabled licenses:
    if (!isEmpty(selectedSubs)) {
      return;
    }
    const currentKRELicenses = availableSubs.filter(
      it => (
        it.feature === OrganizationFeature.ENGINE
          || it.feature === OrganizationFeature.UNLIMITED_ENGINE
      ),
    );
    if (currentKRELicenses.length === 1) {
      handleSelectedSub([{
        feature: currentKRELicenses[0].feature,
        quantity: countUsers,
      }]);
    } else if (currentKRELicenses.length === 2) { // if 2 types of KRE: KRE NL is selected
      handleSelectedSub([{
        feature: OrganizationFeature.ENGINE,
        quantity: countUsers,
      }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countUsers]);

  useEffect(() => {
    handleSelectedProjects([...projects.map(it => it.id)]);
  }, [projects]);

  const onProjectsSelected = (event: SelectChangeEvent<typeof selectedProjects>) => {
    const {
      target: { value },
    } = event;
    if (isArray(value)) {
      const selectedProjectIds = filter(projects, project => value.indexOf(project.id) > -1)
        .map(project => project.id);
      handleSelectedProjects([...selectedProjectIds]);
      return;
    }
    if (typeof value === 'number') {
      handleSelectedProjects([...selectedProjects, value]);
    }
  };

  const handleDeleteProject = (id: number) => () => {
    handleSelectedProjects([...filter(selectedProjects, item => item !== id)]);
  };

  const handleClearSelectedProjects = () => {
    handleSelectedProjects([]);
  };

  const handleSelectAllProjects = () => {
    const projectIds = projects.map(project => project.id);
    handleSelectedProjects(projectIds);
  };

  const onClickRefreshProjects = async () => {
    await handleRefreshProjects();
  };

  const checkProductType = (
    sub: OrganizationFeature,
    tempSelectedSubs: SubscriptionInvitation[],
  ) => {
    // only 1 sub in type should be selected
    // ex: if KSE N-L is selected => KSE Floating should be unselected
    if (kSEproducts.includes(sub)) {
      const remainSub = kSEproducts.find(it => it !== sub);
      if (selectedSubs.find(it => it.feature === remainSub)) {
        const index = selectedSubs.findIndex(it => it.feature === remainSub);
        tempSelectedSubs.splice(index, 1);
      }
    } else if (kREProducts.includes(sub)) {
      const remainSub = kREProducts.find(it => it !== sub);
      if (selectedSubs.find(it => it.feature === remainSub)) {
        const index = selectedSubs.findIndex(it => it.feature === remainSub);
        tempSelectedSubs.splice(index, 1);
      }
    }
  };

  const handleToggleLicense = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sub: OrganizationFeature = event.target.value as OrganizationFeature;
    const tempSelectedSubs = [...selectedSubs];
    const currentAvailableSub = availableSubs.find(it => it.feature === sub);
    const addSub = (quantity: number) => tempSelectedSubs.push({
      feature: sub,
      quantity,
    });

    if (selectedSubs.find(it => it.feature === sub)) {
      const index = selectedSubs.findIndex(it => it.feature === sub);
      tempSelectedSubs.splice(index, 1);
    } else if (!unlimitedAssignableSubs.includes(sub)
      && currentAvailableSub
      && countUsers > currentAvailableSub.remainingLicenses
    ) {
      // validate sub: if sub is unlimited assignable or not
      // unlimited assignable: no need to check currentAvailableSubs and countUsers
      setAllowedSubmit(false);
      checkProductType(sub, tempSelectedSubs);
      addSub(0);
    } else { // unlimited assignable:
      checkProductType(sub, tempSelectedSubs);
      addSub(countUsers);
    }
    // check if org has 2 types of KRE and all are disabled => add KRE NL
    if (kREProducts.includes(sub) && isContainedNLandF()
      && tempSelectedSubs.filter(it => (
        it.feature === OrganizationFeature.ENGINE
          || it.feature === OrganizationFeature.UNLIMITED_ENGINE
      )).length < 1
    ) {
      tempSelectedSubs.push({
        feature: kREProducts.find(it => it !== sub) ?? OrganizationFeature.ENGINE,
        quantity: countUsers,
      });
    }
    // check if there's any sub has assigned quantity = 0 and then allowSubmit or not
    if (!tempSelectedSubs.find(it => it.quantity === 0)) {
      setAllowedSubmit(true);
    }
    handleSelectedSub(tempSelectedSubs);
  };

  const renderProjects = () => (
    projects.map(project => (
      <MenuItem
        id={`user_management.invite.projects_licenses.project_item_${project.id}`}
        key={`projectSelectItem-${project.id}`}
        value={project.id}
        classes={{ selected: classes.projectSelectedItem }}
      >
        <Checkbox
          id={`user_management.invite.projects_licenses.project_item_checkbox_${project.id}`}
          checked={selectedProjects.indexOf(project.id) > -1}
        />
        <ListItemText
          id={`user_management.invite.projects_licenses.project_item_name_${project.id}`}
          primary={project.name}
          className={classes.projectItemText}
        />
      </MenuItem>
    ))
  );

  const renderProjectItem = (id: number) => {
    const projectInfo = find(projects, { id });
    return projectInfo
      ? (
        <Chip
          id={`user_management.invite.projects_licenses.project_chip_${projectInfo.id}`}
          key={`projectChip-${projectInfo.id}`}
          label={projectInfo.name}
          deleteIcon={<ClearIcon />}
          className={classes.projectChip}
          onDelete={handleDeleteProject(id)}
          onMouseDown={event => {
            event.stopPropagation();
          }}
        />
      )
      : <></>;
  };

  const renderProjectPlaceHolder = (selectedProjectIds: number[]) => {
    if (isEmptyProjects) {
      return (
        <Typography id="user_management.invite.projects_licenses.project_not_found_placeholder" className={classes.projectLabel}>
          <FormattedMessage id="user_management.invite.projects_licenses.label_not_found_projects" />
        </Typography>
      );
    }
    if (isSelectAllProjects) {
      return (
        <Typography id="user_management.invite.projects_licenses.project_all_placeholder" className={classes.projectLabel}>
          <FormattedMessage id="user_management.invite.projects_licenses.label_all_projects" />
        </Typography>
      );
    }
    if (isSelectNoProject) {
      return (
        <Typography id="user_management.invite.projects_licenses.project_no_placeholder" className={classes.projectLabel}>
          <FormattedMessage id="user_management.invite.projects_licenses.label_no_project" />
        </Typography>
      );
    }
    return (
      <Box
        id="user_management.invite.projects_licenses.project_box_placeholder"
        className={classes.projectsRoot}
      >
        {selectedProjectIds.map(item => renderProjectItem(item))}
      </Box>
    );
  };

  const renderLinkCreateProject = () => (
    <Link
      id="user-management.invite.projects_licenses.link_create_project"
      href={resolvePath(projectManagementPath, undefined, { orgId })}
      target="child"
      underline="none"
    >
      <Typography
        id="user-management.invite.projects_licenses.link_create_project_text"
        variant="subtitle2"
        color="#1847ca"
      >
        <FormattedMessage id="user_management.invite.projects_licenses.link_create_project" />
      </Typography>
    </Link>
  );

  const renderLinkComponent = (feature: OrganizationFeature) => {
    switch (feature) {
      case OrganizationFeature.KSE:
        return (
          <Link
            id="user-management.invite-user-dialog.kse-info"
            href={PATH_KSE_INFO}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
      case OrganizationFeature.PER_USER_KSE:
        return (
          <Link
            id="user-management.invite-user-dialog.kse-per-user"
            href={PATH_KSE_PER_USER}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
      case OrganizationFeature.UNLIMITED_KSE:
        return (
          <Link
            id="user-management.invite-user-dialog.kse.floating"
            href={PATH_KSE_FLOATING}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
      case OrganizationFeature.UNLIMITED_ENGINE:
        return (
          <Link
            id="user-management.invite-user-dialog.kre-floating"
            href={PATH_UNLIMITED_ENGINE}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
      case OrganizationFeature.ENGINE:
        return (
          <Link
            id="user-management.invite-user-dialog.kre-info"
            href={PATH_KRE_INFO}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
      case OrganizationFeature.TESTOPS_PLATFORM:
        return (
          <Link
            id="user-management.invite-user-dialog.testops-info"
            href={PATH_TESTOPS_PLATFORM_PLAN}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
      default:
        return (
          <Link
            id="user-management.invite-user-dialog.license-overview"
            href={PATH_LICENSE_OVERVIEW}
            target="_blank"
            underline="none"
          >
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Link>
        );
    }
  };

  const renderSubSwitch = (feature: OrganizationFeature, isAutoAssignedKRE: () => boolean) => (
    isAutoAssignedKRE()
      ? (
        <CustomSwitch
          id={`user_management.invite.projects_licenses.switch_${feature}`}
          value={feature}
          defaultChecked
          disabled
        />
      ) : (
        <CustomSwitch
          id={`user_management.invite.projects_licenses.switch_${feature}`}
          value={feature}
          onChange={handleToggleLicense}
          checked={selectedSubs.map(it => it.feature).includes(feature)}
        />
      ));

  const renderSubTitleMsg = (sub: AvailableSubscription) => {
    const featureName = `${OrganizationFeature.getFeatureFullName(sub.feature).replace('(', '').replace(')', '')}`;
    const slotsRemaining = unlimitedAssignableSubs.includes(sub.feature)
      ? intl.formatMessage({ id: 'user_management.invite_users.unlimited' })
      : `${sub.remainingLicenses}/${sub.total}`;
    return `${featureName} (${slotsRemaining})`;
  };

  const renderSubIcon = (feature: OrganizationFeature) => (
    [OrganizationFeature.UNLIMITED_ENGINE, OrganizationFeature.ENGINE]
      .includes(feature) ? (<KREIcon />) : (<KSEIcon />)
  );

  const renderSubMsg = (
    sub: AvailableSubscription,
    isCurrentSubSelected: () => boolean | undefined,
    isAutoAssignedKRE: () => boolean,
    currentSelectedSub?: SubscriptionInvitation,
  ) => {
    if (isCurrentSubSelected() || isAutoAssignedKRE()) {
      // KRE license: auto assigned when it's only 1 subscription (KRE NL or KRE F)
      return (
        <Typography
          id={`user_management.invite.projects_licenses.invite_msg_${sub.feature}`}
          variant="subtitle2"
          className={classes.message}
        >
          <FormattedMessage
            id="user_management.invite_users.invite_message"
            values={{
              countUsers,
              orgName: organization?.name,
              subName: sub.featureHalfFullName,
            }}
          />
        </Typography>
      );
    }
    if (currentSelectedSub && currentSelectedSub.quantity === 0) {
      return (
        <div className={clsx(classes.warning)}>
          <WarningIcon className={classes.warningIcon} />
          <Typography
            id={`user_management.invite.projects_licenses.warning_msg_${sub.feature}`}
            variant="subtitle2"
            className={classes.warningMessage}
          >
            <FormattedMessage id="user_management.invite_users.users_exceed" values={{ br: <br /> }} />
          </Typography>
        </div>
      );
    }
    return (
      <Typography
        id="user_management.invite.projects_licenses.invite_msg_occupied_space"
        variant="subtitle2"
        className={classes.occupiedSpace}
      />
    );
  };

  const renderSubs = () => [
    <div key={`${OrganizationFeature.TESTOPS_PLATFORM}-licenseWrapper`} className={classes.licenseWrapper}>
      <div className={classes.license}>
        <CustomSwitch
          id={`user_management.invite.projects_licenses.switch_${OrganizationFeature.TESTOPS_PLATFORM}`}
          defaultChecked
          disabled
        />
        <div className={classes.licenseInfo}>
          <TestOpsIcon />
          <Typography
            id={`user_management.invite.projects_licenses.feature_name_${OrganizationFeature.TESTOPS_PLATFORM}`}
            className={classes.licenseName}
            variant="subtitle2"
          >
            {`${OrganizationFeature.getFeatureFullName(OrganizationFeature.TESTOPS_PLATFORM)
              .replace('(', '').replace(')', '')}`}
          </Typography>
        </div>
        {
          renderLinkComponent(OrganizationFeature.TESTOPS_PLATFORM)
        }
      </div>
      <Typography
        id={`user_management.invite.projects_licenses.invite_msg_${OrganizationFeature.TESTOPS_PLATFORM}`}
        variant="subtitle2"
        className={classes.message}
      >
        <FormattedMessage
          id="user_management.invite_users.invite_message"
          values={{
            countUsers,
            orgName: organization?.name,
            subName: OrganizationFeature
              .getFeatureHalfFullName(OrganizationFeature.TESTOPS_PLATFORM),
          }}
        />
      </Typography>
    </div>,
    ...availableSubs
      .filter(it => !it.feature.includes(OrganizationFeature.TESTOPS)
        && !isHybridFeature(it.feature)
        && !isVisualTestingFeature(it.feature))
      .map(sub => {
        const currentSelectedSub = selectedSubs.find(it => it.feature === sub.feature);
        const isCurrentSubSelected = () => (currentSelectedSub && currentSelectedSub.quantity > 0);
        const isAutoAssignedKRE = () => kREProducts.includes(sub.feature) && !isContainedNLandF();
        return (
          <div key={`${sub.feature}-licenseWrapper`} className={classes.licenseWrapper}>
            <div className={classes.license}>
              {
                renderSubSwitch(sub.feature, isAutoAssignedKRE)
              }
              <div className={classes.licenseInfo}>
                {
                  renderSubIcon(sub.feature)
                }
                <Typography
                  id={`user_management.invite.projects_licenses.license_name_${sub.feature}`}
                  className={classes.licenseName}
                  variant="subtitle2"
                >
                  {
                    renderSubTitleMsg(sub)
                  }
                </Typography>
              </div>
              {
                renderLinkComponent(sub.feature)
              }
            </div>
            {
              renderSubMsg(sub, isCurrentSubSelected, isAutoAssignedKRE, currentSelectedSub)
            }
          </div>
        );
      }),
  ];

  return (
    <Grid
      id="user_management.invite.projects_licenses.container"
      container
      direction="column"
      className={classes.root}
      alignItems="center"
    >
      <Grid id="user_management.invite.projects_licenses.title_section" item>
        <Typography
          id="user_management.invite.projects_licenses.title_invitation"
          variant="h2"
          className={classes.title}
        >
          <FormattedMessage id="user_management.invite.projects_licenses.title" />
        </Typography>
      </Grid>
      <Grid id="user_management.invite.projects_licenses.input_section" item>
        <Grid
          id="user_management.invite.projects_licenses.project_gird_container"
          item
          justifyContent="center"
          className={classes.sectionProjectWrapper}
        >
          <Typography
            id="user_management.invite.projects_licenses.project_label_container"
            variant="body1"
            className={classes.labelSection}
          >
            <FormattedMessage id="user_management.invite.projects_licenses.label_add_projects" />
          </Typography>
          <div className={classes.projectContainer}>
            <div className={classes.licenseWrapper}>
              <div className={classes.project}>
                <Select
                  id="user_management.invite.projects_licenses.project_select_box"
                  multiple
                  displayEmpty
                  disabled={isEmptyProjects}
                  value={selectedProjects}
                  onChange={onProjectsSelected}
                  renderValue={renderProjectPlaceHolder}
                  className={classes.selectionProjects}
                  MenuProps={{ classes: { paper: classes.menuPaper }, autoFocus: false }}
                >
                  <MenuItem
                    id="user_management.invite.projects_licenses.project_action_item"
                    className={classes.selectionAction}
                    onClick={isEmpty(selectedProjects)
                      ? handleSelectAllProjects
                      : handleClearSelectedProjects}
                  >
                    <span>
                      { intl.formatMessage({ id: isEmpty(selectedProjects)
                        ? 'user_management.invite.projects_licenses.select_all_projects'
                        : 'user_management.invite.projects_licenses.deselect_projects' })}
                    </span>
                  </MenuItem>
                  {
                    renderProjects()
                  }
                </Select>
                <IconButton
                  id="user_management.invite.projects_licenses.refresh_project_button"
                  onClick={onClickRefreshProjects}
                >
                  <RefreshIcon className={classes.iconRefresh} />
                </IconButton>
              </div>
            </div>
          </div>
          {
            renderLinkCreateProject()
          }
        </Grid>
        <Grid
          id="user_management.invite.projects_licenses.license_gird_container"
          item
          justifyContent="center"
          className={classes.sectionLicenseWrapper}
        >
          <Typography
            id="user_management.invite.projects_licenses.license_label_container"
            variant="body1"
            className={classes.labelSection}
          >
            <FormattedMessage id="user_management.invite.projects_licenses.label_select_licenses" />
          </Typography>
          <div className={classes.licenseContainer}>
            { renderSubs() }
          </div>
        </Grid>
      </Grid>
      <div className={classes.buttonContainer}>
        <Button
          id="user_management.invite_users.confirm_button"
          variant="contained"
          color="primary"
          className={clsx(classes.button, classes.buttonConfirm)}
          disabled={!allowedSubmit}
          onClick={onSubmit}
        >
          <FormattedMessage id="user_management.invite_users.confirm" />
        </Button>
        <Button
          id="user_management.invite_users.back_button"
          className={clsx(classes.button, classes.buttonBack)}
          variant="text"
          onClick={onBack}
        >
          <FormattedMessage id="user_management.invite_users.back" />
        </Button>
      </div>
    </Grid>
  );
};
export default SelectProjectsAndLicenses;
